import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from '@angular/core';

import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {MaterialModule} from '../../../material.module';
import {
  ConnectAuthType,
  ConnectProviderCatalog,
  ConnectProviderDef,
  ConnectProvidersService,
  ConnectUtilityDescriptor,
  ConnectUtilityKind,
} from '../../../services/connect/connect-providers.service';
import {ConnectSecretMasked, ConnectSecretsService} from '../../../services/connect/connect-secrets.service';
import {of, Subscription} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';
import {ColorFieldComponent} from '../../../shared/color-field/color-field.component';
import {ToastService} from '../../../services/layout/toast.service';

type SecretKey = 'client_id' | 'client_secret';

@Component({
  selector: 'app-connect-provider-detail',
  standalone: true,
    imports: [RouterModule, ReactiveFormsModule, MaterialModule, ColorFieldComponent],
  templateUrl: './connect-provider-detail.component.html',
  styleUrls: ['../connect-shared.scss', './connect-provider-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'connect-theme'},
})
export class ConnectProviderDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly providersService = inject(ConnectProvidersService);
  private readonly secretsService = inject(ConnectSecretsService);
  private readonly toastService = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sub = new Subscription();

  catalog: ConnectProviderCatalog | null = null;
  provider: ConnectProviderDef | null = null;
  utilities: ConnectUtilityDescriptor[] = [];

  loading = false;
  saving = false;
  deleting = false;
  error: string | null = null;

  authSupported = new Set<ConnectAuthType>(['apiKey']);
  defaultAuth: ConnectAuthType = 'apiKey';

  readonly authOptions: ConnectAuthType[] = ['oauth', 'apiKey', 'bearer', 'basic', 'sftp'];
  readonly utilityKinds: ConnectUtilityKind[] = ['select', 'multiselect', 'autocomplete'];

  providerForm = this.fb.group({
    id: ['', Validators.required],
    name: ['', Validators.required],
    ref: ['', Validators.required],
    docsUrl: [''],
    logo: [''],
    description: [''],
    tags: [''],
    paletteColor: [''],
    paletteBackground: [''],
    paletteBorder: [''],
    paletteText: [''],
  });

  oauthForm = this.fb.group({
    authorization_url: [''],
    token_url: [''],
    app_id: [''],
    required_scopes: [''],
    optional_scopes: [''],
    use_pkce: [true],
    extra_params: ['{}'],
  });

  secretState: Record<
    SecretKey,
    {masked: string; hasSecret: boolean; loading: boolean; saving: boolean; value: string; error: string | null}
  > = {
    client_id: {masked: '', hasSecret: false, loading: false, saving: false, value: '', error: null},
    client_secret: {masked: '', hasSecret: false, loading: false, saving: false, value: '', error: null},
  };

  private initialProviderId: string | null = null;
  private hasExistingProvider = false;

  get isNew(): boolean {
    return !this.hasExistingProvider;
  }

  getPaletteControl(key: 'paletteBackground' | 'paletteBorder' | 'paletteText'): FormControl<string | null> {
    return this.providerForm.get(key) as FormControl<string | null>;
  }

  ngOnInit(): void {
    this.sub.add(
      this.route.paramMap.subscribe(params => {
        const id = params.get('id') || 'new';
        this.loadProvider(id);
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  save(): void {
    if (!this.providerForm.valid) {
      this.providerForm.markAllAsTouched();
      return;
    }
    const palette = this.buildPalette();
    const supported = Array.from(this.authSupported) as ConnectAuthType[];
    if (supported.length === 0) supported.push('apiKey');
    const defaultAuth = supported.includes(this.defaultAuth) ? this.defaultAuth : supported[0];

    const tags = (this.providerForm.value.tags || '')
      .split(',')
      .map(t => (t || '').trim())
      .filter(Boolean);

    const requiredScopes = this.parseScopes(this.oauthForm.value.required_scopes);
    const optionalScopes = this.parseScopes(this.oauthForm.value.optional_scopes);
    let extraParams: Record<string, string> = {};
    if (this.oauthForm.value.extra_params) {
      try {
        const parsed = JSON.parse(this.oauthForm.value.extra_params);
        if (parsed && typeof parsed === 'object') {
          extraParams = parsed as Record<string, string>;
        } else {
          throw new Error('Invalid JSON');
        }
      } catch {
        this.error = 'Extra params must be valid JSON.';
        this.cdr.markForCheck();
        return;
      }
    }

    const oauthEnabled = this.authSupported.has('oauth');
    const oauth2 = oauthEnabled
      ? {
          ...this.provider?.oauth2,
          authorization_url: this.oauthForm.value.authorization_url || undefined,
          token_url: this.oauthForm.value.token_url || undefined,
          app_id: this.oauthForm.value.app_id || undefined,
          required_scopes: requiredScopes.length || this.provider?.oauth2?.required_scopes ? requiredScopes : undefined,
          optional_scopes: optionalScopes.length || this.provider?.oauth2?.optional_scopes ? optionalScopes : undefined,
          use_pkce: this.oauthForm.value.use_pkce ?? true,
          extra_params: extraParams,
        }
      : undefined;

    const next: ConnectProviderDef = {
      ...this.provider,
      id: (this.providerForm.value.id || '').trim(),
      name: (this.providerForm.value.name || '').trim(),
      ref: (this.providerForm.value.ref || '').trim(),
      docsUrl: (this.providerForm.value.docsUrl || '').trim(),
      logo: (this.providerForm.value.logo || '').trim(),
      description: (this.providerForm.value.description || '').trim(),
      tags,
      palette,
      auth: {supported, default: defaultAuth},
      oauth2,
      utilities: this.utilities.map(u => this.normalizeUtility(u)),
    };

    const providers = [...(this.catalog?.providers || [])];
    const matchId = this.initialProviderId || next.id;
    const idx = providers.findIndex(p => p.id === matchId);
    if (idx >= 0) {
      providers[idx] = next;
    } else {
      providers.push(next);
    }

    this.saving = true;
    this.error = null;
    this.providersService
      .updateCatalog({version: 'v1', providers})
      .pipe(
        catchError(err => {
          console.error('Failed to save provider', err);
          this.error = 'Could not save provider.';
          this.cdr.markForCheck();
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(saved => {
        if (!saved) return;
        this.catalog = saved;
        this.initialProviderId = next.id;
        this.hasExistingProvider = true;
        const refreshed = saved.providers.find(p => p.id === next.id) || next;
        this.setProvider(refreshed, true);
        this.toastService.showSuccess('Provider saved', 2000);
      });
  }

  deleteProvider(): void {
    if (!this.catalog) {
      this.router.navigate(['/connect/settings']);
      return;
    }
    const id = this.initialProviderId || this.providerForm.value.id || '';
    if (!id) {
      this.router.navigate(['/connect/settings']);
      return;
    }
    const confirmed = window.confirm('Remove this provider from the catalog?');
    if (!confirmed) return;
    this.deleting = true;
    const providers = (this.catalog.providers || []).filter(p => p.id !== id);
    this.providersService
      .updateCatalog({version: 'v1', providers})
      .pipe(
        catchError(err => {
          console.error('Failed to delete provider', err);
          this.error = 'Could not delete provider.';
          this.cdr.markForCheck();
          return of(null);
        }),
        finalize(() => {
          this.deleting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(result => {
        if (!result) return;
        this.toastService.showSuccess('Provider deleted', 2000);
        this.router.navigate(['/connect/settings']);
      });
  }

  toggleAuth(type: ConnectAuthType, checked: boolean): void {
    const next = new Set(this.authSupported);
    if (checked) next.add(type);
    else next.delete(type);
    if (next.size === 0) {
      next.add('apiKey');
    }
    this.authSupported = next;
    if (!this.authSupported.has(this.defaultAuth)) {
      this.defaultAuth = Array.from(this.authSupported)[0] as ConnectAuthType;
    }
    this.cdr.markForCheck();
  }

  setDefaultAuth(type: ConnectAuthType): void {
    if (!this.authSupported.has(type)) {
      this.authSupported.add(type);
    }
    this.defaultAuth = type;
    this.cdr.markForCheck();
  }

  addUtility(): void {
    const baseRef = (this.providerForm.value.id || 'provider').toLowerCase();
    const empty: ConnectUtilityDescriptor = {
      id: `${baseRef}.utility`,
      name: 'New utility',
      kind: 'select',
      operation: `${baseRef}.operation`,
      item: {idField: 'id', labelField: 'label'},
      pagination: false,
    };
    this.utilities = [...this.utilities, empty];
    this.cdr.markForCheck();
  }

  updateUtility(idx: number, partial: Partial<ConnectUtilityDescriptor>): void {
    const copy = [...this.utilities];
    const curr = copy[idx];
    if (!curr) return;
    copy[idx] = {
      ...curr,
      ...partial,
      item: partial.item ? {...curr.item, ...partial.item} : curr.item,
    } as ConnectUtilityDescriptor;
    this.utilities = copy;
    this.cdr.markForCheck();
  }

  updateUtilityItemField(idx: number, key: 'idField' | 'labelField', value: string): void {
    this.updateUtility(idx, {item: {...this.utilities[idx]?.item, [key]: value}});
  }

  updateUtilityMeta(idx: number, raw: string): void {
    const metaFields = raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    this.updateUtility(idx, {item: {...this.utilities[idx]?.item, metaFields}});
  }

  toggleUtilityPagination(idx: number, checked: boolean): void {
    this.updateUtility(idx, {pagination: checked});
  }

  removeUtility(idx: number): void {
    const copy = [...this.utilities];
    copy.splice(idx, 1);
    this.utilities = copy;
    this.cdr.markForCheck();
  }

  saveSecret(key: SecretKey): void {
    const providerId = (this.providerForm.value.id || '').trim();
    if (!providerId) {
      this.secretState[key].error = 'Save the provider before adding secrets.';
      this.cdr.markForCheck();
      return;
    }
    const value = (this.secretState[key].value || '').trim();
    if (!value) {
      this.secretState[key].error = 'Enter a value to store.';
      this.cdr.markForCheck();
      return;
    }
    this.secretState[key].saving = true;
    this.secretState[key].error = null;
    const scope = this.secretsService.secretScopeForProvider(providerId);
    this.secretsService
      .setSecret(scope, key, value)
      .pipe(
          map(() => true),
        catchError(err => {
          console.error('Failed to save secret', err);
          this.secretState[key].error = 'Could not save secret.';
          this.cdr.markForCheck();
            return of(false);
        }),
        finalize(() => {
          this.secretState[key].saving = false;
          this.cdr.markForCheck();
        }),
      )
        .subscribe(success => {
            if (!success) return;
        this.secretState[key].value = '';
        this.loadSecret(providerId, key);
        this.toastService.showSuccess(key === 'client_id' ? 'Client ID saved' : 'Client secret saved', 2000);
      });
  }

  secretPlaceholder(key: SecretKey): string {
    if (this.secretState[key].loading) return 'Loading...';
    if (this.secretState[key].hasSecret) return this.secretState[key].masked || 'Stored';
    return 'Not set';
  }

  oauthEnabled(): boolean {
    return this.authSupported.has('oauth');
  }

  private loadProvider(id: string): void {
    this.loading = true;
    this.error = null;
    this.providersService
      .fetchCatalog()
      .pipe(
        catchError(err => {
          console.error('Failed to load providers', err);
          this.error = 'Could not load provider catalog.';
          this.cdr.markForCheck();
          return of({version: 'v1', providers: []} as ConnectProviderCatalog);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(catalog => {
        this.catalog = catalog;
        const existing = catalog.providers.find(p => p.id === id);
        const provider = existing || this.buildEmptyProvider(id === 'new' ? 'new-provider' : id);
        this.initialProviderId = existing ? existing.id : null;
        this.hasExistingProvider = Boolean(existing);
        this.setProvider(provider, Boolean(existing));
      });
  }

  private setProvider(provider: ConnectProviderDef, existing: boolean): void {
    this.provider = {...provider};
    const supportedRaw = provider.auth?.supported || [];
    const filteredSupported = supportedRaw.filter((a): a is ConnectAuthType => this.authOptions.includes(a as ConnectAuthType));
    const supported = filteredSupported.length ? filteredSupported : (['apiKey'] as ConnectAuthType[]);
    this.authSupported = new Set<ConnectAuthType>(supported);
    this.defaultAuth = this.authSupported.has(provider.auth?.default as ConnectAuthType)
      ? (provider.auth?.default as ConnectAuthType)
      : (Array.from(this.authSupported)[0] as ConnectAuthType);

    this.utilities = [...(provider.utilities || [])];

    this.providerForm.reset({
      id: provider.id,
      name: provider.name,
      ref: provider.ref,
      docsUrl: provider.docsUrl || '',
      logo: provider.logo || '',
      description: provider.description || '',
      tags: (provider.tags || []).join(', '),
      paletteColor: provider.palette?.color || '',
      paletteBackground: provider.palette?.background || '',
      paletteBorder: provider.palette?.border || '',
      paletteText: provider.palette?.text || '',
    });
    this.providerForm.markAsPristine();

    const oauth = provider.oauth2 || {};
    this.oauthForm.reset({
      authorization_url: oauth.authorization_url || '',
      token_url: oauth.token_url || '',
      app_id: oauth.app_id || '',
      required_scopes: (oauth.required_scopes || oauth.default_scopes || []).join(' '),
      optional_scopes: (oauth.optional_scopes || []).join(' '),
      use_pkce: oauth.use_pkce ?? true,
      extra_params: JSON.stringify(oauth.extra_params || {}, null, 2),
    });
    this.oauthForm.markAsPristine();

    this.resetSecretState();
    if (existing) {
      this.loadSecret(provider.id, 'client_id');
      this.loadSecret(provider.id, 'client_secret');
    }
    this.cdr.markForCheck();
  }

  private buildPalette(): ConnectProviderDef['palette'] | undefined {
    const palette = {
      color: (this.providerForm.value.paletteColor || '').trim(),
      background: (this.providerForm.value.paletteBackground || '').trim(),
      border: (this.providerForm.value.paletteBorder || '').trim(),
      text: (this.providerForm.value.paletteText || '').trim(),
    };
    const hasAny = Object.values(palette).some(Boolean);
    return hasAny ? palette : undefined;
  }

  private normalizeUtility(u: ConnectUtilityDescriptor): ConnectUtilityDescriptor {
    const meta = (u.item?.metaFields || []).filter(Boolean);
    const pageSizeNum = Number(u.pageSize);
    const pageSize = Number.isFinite(pageSizeNum) && pageSizeNum > 0 ? pageSizeNum : undefined;
    return {
      ...u,
      id: (u.id || '').trim() || 'new.utility',
      name: (u.name || '').trim() || 'Utility',
      kind: (u.kind as ConnectUtilityKind) || 'select',
      operation: (u.operation || '').trim(),
      item: {
        idField: u.item?.idField || 'id',
        labelField: u.item?.labelField || 'label',
        metaFields: meta.length ? meta : undefined,
      },
      pagination: Boolean(u.pagination),
      pageSize,
    };
  }

  private parseScopes(raw: string | null | undefined): string[] {
    if (!raw) return [];
    return raw
      .split(/[,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
  }

  private buildEmptyProvider(id: string): ConnectProviderDef {
    return {
      id,
      name: 'New Provider',
      ref: `${id}.ref`,
      description: '',
      tags: [],
      docsUrl: '',
      logo: '',
      auth: {supported: ['apiKey'], default: 'apiKey'},
      utilities: [],
    };
  }

  private resetSecretState(): void {
    this.secretState = {
      client_id: {masked: '', hasSecret: false, loading: false, saving: false, value: '', error: null},
      client_secret: {masked: '', hasSecret: false, loading: false, saving: false, value: '', error: null},
    };
  }

  private loadSecret(providerId: string, key: SecretKey): void {
    const scope = this.secretsService.secretScopeForProvider(providerId);
    this.secretState[key].loading = true;
    this.secretState[key].error = null;
    this.secretsService
      .getMaskedSecret(scope, key)
      .pipe(
        catchError(err => {
          console.error('Failed to fetch secret', err);
          this.secretState[key].error = 'Could not load secret.';
          this.cdr.markForCheck();
          return of(null as unknown as ConnectSecretMasked);
        }),
        finalize(() => {
          this.secretState[key].loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(secret => {
        if (!secret) return;
        this.secretState[key].masked = secret.masked;
        this.secretState[key].hasSecret = secret.hasSecret;
        this.cdr.markForCheck();
      });
  }
}
